import React, { useCallback, useEffect, useState } from 'react';
import { Download, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { AdminApi } from '../../services/api';
import { Button } from '../../shared/ui';
import type { CannedResponse } from '../../types';
import { lireEtiquettes, modelesAImporter } from './reponsesTypesDepart';

// Mobile et tablette : cibles tactiles d'au moins 40 px, boutons de validation
// empilés pleine largeur sur téléphone. À partir de sm / lg, rendu d'origine.
const TACTILE = 'min-h-[40px] lg:min-h-0';
// Sur téléphone, les boutons restent collés en bas de la fenêtre qui défile.
const ACTIONS_FENETRE = 'flex flex-col gap-2 sm:flex-row sm:justify-end max-sm:sticky max-sm:-bottom-5 max-sm:z-10 max-sm:bg-white max-sm:-mx-5 max-sm:px-5 max-sm:pb-5 max-sm:pt-3 max-sm:-mb-5';
const BOUTON_FENETRE = `w-full sm:w-auto ${TACTILE}`;

// 16 px sur téléphone : pas de zoom automatique d'iOS au focus.
const champ = 'mt-1 block w-full rounded-lg border border-trait bg-white py-2 px-3 text-base sm:text-sm text-encre placeholder:text-graphite/70 focus:border-sapin-600 focus:ring-2 focus:ring-sapin-600/20 focus:outline-none';

interface Brouillon {
  id: string | null;
  title: string;
  content: string;
  etiquettes: string;
}

const VIDE: Brouillon = { id: null, title: '', content: '', etiquettes: '' };

/**
 * Paramètres > Réponses types : liste, création, modification, suppression
 * (routes /admin/canned-responses), et import des réponses de départ.
 * Ce sont ces réponses que l'agent insère par « / » dans la conversation.
 */
export const GestionReponsesTypes: React.FC = () => {
  const [liste, setListe] = useState<CannedResponse[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    try {
      setListe(await AdminApi.getCannedResponses());
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Les réponses types n\'ont pas pu être chargées.');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const aImporter = modelesAImporter(liste);

  const importer = async () => {
    setEnCours(true);
    setErreur(null);
    setAvis(null);
    let crees = 0;
    try {
      // Relu juste avant : un collègue a pu importer entre-temps.
      const actuelles = await AdminApi.getCannedResponses();
      for (const m of modelesAImporter(actuelles)) {
        await AdminApi.createCannedResponse(m);
        crees++;
      }
      setAvis(crees === 0 ? 'Les réponses de départ sont déjà en base.' : `${crees} réponse${crees > 1 ? 's' : ''} type${crees > 1 ? 's' : ''} ajoutée${crees > 1 ? 's' : ''}.`);
    } catch (e) {
      setErreur(`Import interrompu après ${crees} réponse${crees > 1 ? 's' : ''} : ${e instanceof Error ? e.message : 'erreur'}. Relancez l'import : les réponses déjà créées ne seront pas dupliquées.`);
    } finally {
      setEnCours(false);
      await charger();
    }
  };

  const enregistrer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brouillon) return;
    const title = brouillon.title.trim();
    const content = brouillon.content.trim();
    if (!title || !content) {
      setErreur('Le titre et le texte sont obligatoires.');
      return;
    }
    setEnCours(true);
    setErreur(null);
    setAvis(null);
    try {
      const data = { title, content, tags: lireEtiquettes(brouillon.etiquettes) };
      if (brouillon.id) await AdminApi.updateCannedResponse(brouillon.id, data);
      else await AdminApi.createCannedResponse(data);
      setBrouillon(null);
      setAvis(brouillon.id ? 'Réponse type modifiée.' : 'Réponse type créée.');
      await charger();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Enregistrement impossible');
    } finally {
      setEnCours(false);
    }
  };

  const supprimer = async (r: CannedResponse) => {
    if (!window.confirm(`Supprimer la réponse type « ${r.title} » ?`)) return;
    setEnCours(true);
    setErreur(null);
    setAvis(null);
    try {
      await AdminApi.deleteCannedResponse(r.id);
      setAvis('Réponse type supprimée.');
      await charger();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Suppression impossible');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Réponses types</h2>
          <p className="text-sm text-graphite">Insérées dans la conversation en tapant « / ». Variables : {'{{customerName}}'}, {'{{ticketRef}}'}, {'{{agentName}}'}.</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {aImporter.length > 0 && !chargement && (
            <Button variant="secondary" size="sm" onClick={importer} disabled={enCours} className={`grow sm:grow-0 ${TACTILE}`}>
              <Download className="w-4 h-4 mr-1" />Importer les réponses de départ ({aImporter.length})
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={() => { setBrouillon(VIDE); setAvis(null); setErreur(null); }} disabled={enCours} className={`grow sm:grow-0 ${TACTILE}`}>
            <Plus className="w-4 h-4 mr-1" />Nouvelle réponse
          </Button>
        </div>
      </div>

      {erreur && <p role="alert" className="text-sm text-red-700">{erreur}</p>}
      {avis && <p role="status" className="text-sm text-sapin-700">{avis}</p>}

      {brouillon && (
        <form onSubmit={enregistrer} className="space-y-3 rounded-lg border border-trait bg-brume p-3 sm:p-4" aria-label="Réponse type">
          <label className="block"><span className="text-xs font-semibold text-graphite">Titre</span>
            <input value={brouillon.title} onChange={e => setBrouillon({ ...brouillon, title: e.target.value })} maxLength={200} className={champ} /></label>
          <label className="block"><span className="text-xs font-semibold text-graphite">Texte</span>
            <textarea value={brouillon.content} onChange={e => setBrouillon({ ...brouillon, content: e.target.value })} rows={8} maxLength={10000} className={`${champ} resize-y`} /></label>
          <label className="block"><span className="text-xs font-semibold text-graphite">Étiquettes (séparées par des virgules)</span>
            <input value={brouillon.etiquettes} onChange={e => setBrouillon({ ...brouillon, etiquettes: e.target.value })} className={champ} placeholder="Ex. : gar, garantie" /></label>
          <div data-testid="actions-fenetre" className={ACTIONS_FENETRE}>
            <Button variant="secondary" size="sm" onClick={() => setBrouillon(null)} disabled={enCours} className={BOUTON_FENETRE}>Annuler</Button>
            <Button type="submit" variant="primary" size="sm" disabled={enCours} className={BOUTON_FENETRE}>{enCours ? 'Enregistrement…' : 'Enregistrer'}</Button>
          </div>
        </form>
      )}

      {chargement ? (
        <p className="flex items-center gap-2 text-sm text-graphite"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</p>
      ) : liste.length === 0 ? (
        <p className="text-sm text-graphite">Aucune réponse type en base. Importez les réponses de départ ou créez-en une.</p>
      ) : (
        <ul className="divide-y divide-trait rounded-lg border border-trait">
          {liste.map(r => (
            <li key={r.id} className="flex items-start gap-1 sm:gap-3 p-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-encre break-words">{r.title}</p>
                <p className="text-xs text-graphite line-clamp-2 whitespace-pre-line">{r.content}</p>
                {r.tags.length > 0 && <p className="mt-1 text-xs text-graphite">{r.tags.map(t => `#${t}`).join(' ')}</p>}
              </div>
              <button type="button" aria-label={`Modifier « ${r.title} »`} disabled={enCours}
                onClick={() => { setBrouillon({ id: r.id, title: r.title, content: r.content, etiquettes: r.tags.join(', ') }); setAvis(null); setErreur(null); }}
                className="p-3 lg:p-2 shrink-0 text-graphite hover:text-encre hover:bg-brume rounded-lg"><Pencil className="w-4 h-4" /></button>
              <button type="button" aria-label={`Supprimer « ${r.title} »`} disabled={enCours} onClick={() => supprimer(r)}
                className="p-3 lg:p-2 shrink-0 text-graphite hover:text-red-700 hover:bg-brume rounded-lg"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GestionReponsesTypes;
